import { select } from "redux-saga/effects";

import DistanceRecord from "@mapmycustomers/shared/types/entity/Distance";
import Organization from "@mapmycustomers/shared/types/Organization";
import DateRange from "@mapmycustomers/shared/types/range/DateRange";
import User from "@mapmycustomers/shared/types/User";

import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import { callApi } from "@app/store/api/callApi";
import { getOrganizationId } from "@app/store/iam";
import DistanceTravelledCardConfiguration, {
  DistanceTravelledAggregationType,
} from "@app/types/dashboard/cards/distanceTravelled/DistanceTravelledCardConfiguration";
import DistanceTravelledCardData from "@app/types/dashboard/cards/distanceTravelled/DistanceTravelledCardData";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import { formatDate } from "@app/util/formatters";

import { Granularity } from "../common";

import fillDistanceBuckets, { DistanceAggBucket, DistanceMetric } from "./fillDistanceBuckets";
import getDistanceBuckets, { DailyGranularity } from "./getDistanceBuckets";

// `date` is a zone-less PG date column: date-only edges, and no $timezone (DistanceFilter does not
// declare it in dateOnlyColumns, so a client zone shifts the day edges). Field Time does the same.
export const buildDistanceFilters = (range: DateRange, userIds: User["id"][]) => ({
  date: {
    $gte: formatDate(range.startDate, "yyyy-MM-dd"),
    $lte: formatDate(range.endDate, "yyyy-MM-dd"),
  },
  ...(userIds.length ? { userId: { $in: userIds } } : {}),
});

// Both ranges bucket identically so the trend compares like with like: in AVG mode the card's
// value is a sum of per-bucket averages, which a range total would not match. The average is the
// server's, so the card and its breakdown cannot drift from each other.
function* fetchDistanceHistory(params: {
  granularity: DailyGranularity;
  metric: DistanceMetric;
  orgId: Organization["id"];
  range: DateRange;
  userIds: User["id"][];
}): Generator<unknown, NonNullable<DistanceTravelledCardData["history"]>, never> {
  const { granularity, metric, orgId, range, userIds } = params;
  const response: AggregatedListResponse<DistanceRecord, DistanceAggBucket[]> = yield callApi(
    "fetchDistance",
    orgId,
    {
      // No offset or timezone: either makes the server emit SQL it cannot type against a `date`
      // column and 500s (verified on api-test), so weeks come back Monday-truncated.
      $aggs: {
        [metric]: ["totalDistance"],
        primaryGroup: { field: "date", interval: granularity },
      },
      $filters: buildDistanceFilters(range, userIds),
      $limit: 0,
    }
  );

  return fillDistanceBuckets(
    response.aggregations,
    getDistanceBuckets(range, granularity),
    granularity,
    metric
  );
}

// Fallback for orgs without Mileage Tracker: totalDistance falls back to GPS-derived passive
// distance when no trips exist, so these orgs get an estimate instead of a permanent zero.
export function* fetchDistanceTravelledFromDistances(params: {
  configuration: DistanceTravelledCardConfiguration;
  granularity: Granularity;
  userIds: User["id"][];
}): Generator<unknown, DistanceTravelledCardData, never> {
  const { configuration, granularity, userIds } = params;
  const { criteria, dateRange, preferences } = configuration;
  const orgId: Organization["id"] = yield select(getOrganizationId);

  // rows are per user-day, so an hourly range collapses to the single day it covers
  const isHourly = granularity === "hour";
  const bucketGranularity: DailyGranularity = isHourly ? "day" : granularity;
  const metric: DistanceMetric =
    criteria.aggregationType === DistanceTravelledAggregationType.AVG ? "avg" : "sum";

  const history = yield* fetchDistanceHistory({
    granularity: bucketGranularity,
    metric,
    orgId,
    range: getDatesRange(dateRange.range, dateRange.subRange),
    userIds,
  });

  let runningTotal = 0;
  const cumulativeHistory = history.map((bucket) => {
    runningTotal += bucket.distance;
    return { ...bucket, distance: runningTotal };
  });

  const previousHistory = preferences.showTrend
    ? yield* fetchDistanceHistory({
        granularity: bucketGranularity,
        metric,
        orgId,
        range: getDatesRange(dateRange.range, dateRange.subRange, true),
        userIds,
      })
    : undefined;

  // one bucket is not a chart, so an hourly range shows the total only
  const showChart = preferences.showChart && !isHourly;

  return {
    distance: history.reduce((sum, bucket) => sum + bucket.distance, 0),
    history: showChart ? (preferences.cumulative ? cumulativeHistory : history) : undefined,
    isEstimated: true,
    previousDistance: previousHistory?.reduce((sum, bucket) => sum + bucket.distance, 0),
  };
}

export default fetchDistanceTravelledFromDistances;
